import InsightItem from 'components/InsightItem';
import SegmentedBar from 'components/SegmentedBar';
import { useAtomValue } from 'jotai';
import React from 'react';
import { Card } from 'reactstrap';

import {
  enabledColumnKeysAtom,
  repositoriesDataAtom,
  selectedStateFiltersListAtom,
} from './state';

interface Insights {
  status_breakdown: {
    active: number;
    inactive: number;
    error: number;
    disconnected: number;
    archived: number;
  };
  severity_breakdown: {
    bugs: {
      urgent: number;
      critical: number;
      serious: number;
      medium: number;
      minimal: number;
    };
    vulnerabilities: {
      urgent: number;
      critical: number;
      serious: number;
      medium: number;
      minimal: number;
    };
  };
  repo_type_breakdown: {
    azure: number;
    bitbucket: number;
    github: number;
    gitlab: number;
  };
  accounts_count: number;
  total_vulnerabilities: number;
  total_bugs: number;
}

const RepoInsights: React.FC = () => {
  const listingData = useAtomValue(repositoriesDataAtom);
  const selectedStateFiltersList = useAtomValue(selectedStateFiltersListAtom);
  const insights: Insights | undefined = listingData?.insights;

  const enabledColumnKeys = useAtomValue(enabledColumnKeysAtom);
  const isBugColumnEnabled = enabledColumnKeys.includes('bugs_count');

  if (!listingData || !insights || Object.keys(insights).length === 0) {
    return <div>Loading...</div>; // Add a loading state
  }

  const getSeverityCount = (
    severity: 'urgent' | 'critical' | 'serious' | 'medium' | 'minimal',
  ) => {
    const { bugs, vulnerabilities } = insights.severity_breakdown;
    return isBugColumnEnabled
      ? bugs[severity] + vulnerabilities[severity]
      : vulnerabilities[severity];
  };

  // Extract stats from insights
  const stats = {
    active: insights.status_breakdown.active,
    inactive: insights.status_breakdown.inactive,
    error: insights.status_breakdown.error,
    disconnected: insights.status_breakdown.disconnected,
    archived: insights.status_breakdown.archived,
    urgent: getSeverityCount('urgent'),
    critical: getSeverityCount('critical'),
    serious: getSeverityCount('serious'),
    medium: getSeverityCount('medium'),
    minimal: getSeverityCount('minimal'),
    azure: insights.repo_type_breakdown.azure,
    bitbucket: insights.repo_type_breakdown.bitbucket,
    github: insights.repo_type_breakdown.github,
    gitlab: insights.repo_type_breakdown.gitlab,
    total_bugs: insights.total_bugs,
    total_vulnerabilities: insights.total_vulnerabilities,
    accounts_count: insights.accounts_count,
  };

  return (
    <div className="container-fluid border border-light rounded p-0 mb-1">
      <Card
        className="mb-0 px-3 py-1"
        style={{ backgroundColor: 'rgba(34, 42, 66, 0.02)' }}
      >
        <div className="d-flex flex-wrap align-items-center">
          {/* Severity */}
          <div
            className="mr-2 pr-2 flex-grow-1 border-right"
            style={{ borderColor: '#dee2e6' }}
          >
            <h6 className="mb-0">Severity:</h6>
            <SegmentedBar
              height="5px"
              width="100%"
              segments={[
                {
                  color: 'urgent',
                  value: stats.urgent,
                  label: 'Urgent',
                },
                {
                  color: 'critical',
                  value: stats.critical,
                  label: 'Critical',
                },
                {
                  color: 'serious',
                  value: stats.serious,
                  label: 'Serious',
                },
                {
                  color: 'medium',
                  value: stats.medium,
                  label: 'Medium',
                },
                {
                  color: 'minimal',
                  value: stats.minimal,
                  label: 'Minimal',
                },
              ]}
              labelDisplay="value"
              tooltip={`Urgent: ${stats.urgent}, Critical: ${stats.critical}, Serious: ${stats.serious}, Medium: ${stats.medium}, Minimal: ${stats.minimal}`}
            />
          </div>

          {/* Repo Status */}
          <div
            className="mr-2 pr-2 flex-grow-1 border-right"
            style={{ borderColor: '#dee2e6' }}
          >
            <h6 className="mb-0">Status:</h6>
            <SegmentedBar
              height="5px"
              width="100%"
              segments={[
                {
                  color: 'success' as const,
                  value: stats.active,
                  label: 'Active',
                },
                {
                  color: 'warning' as const,
                  value: stats.inactive,
                  label: 'Inactive',
                },
              ]}
              labelDisplay="value"
              tooltip={
                <>
                  Total Repositories: {stats.active + stats.inactive}
                  <br />
                  Active: {stats.active}
                  <br />
                  Inactive: {stats.inactive}
                  <br />
                  Error: {stats.error}
                  <br />
                  Disconnected: {stats.disconnected}
                  <br />
                  Archived: {stats.archived}
                </>
              }
            />
          </div>

          {/* Count Summaries */}
          {stats.azure > 0 && (
            <InsightItem
              label="Azure"
              value={stats.azure}
              tooltip="Total number of Azure repositories"
            />
          )}
          {stats.bitbucket > 0 && (
            <InsightItem
              label="Bitbucket"
              value={stats.bitbucket}
              tooltip="Total number of Bitbucket repositories"
            />
          )}
          {stats.github > 0 && (
            <InsightItem
              label="Github"
              value={stats.github}
              tooltip="Total number of Github repositories"
            />
          )}
          {stats.gitlab > 0 && (
            <InsightItem
              label="Gitlab"
              value={stats.gitlab}
              tooltip="Total number of Gitlab repositories"
            />
          )}
          {stats.accounts_count > 0 && (
            <InsightItem
              label="Accounts"
              value={stats.accounts_count}
              tooltip="Total accounts with one or more connected repositories"
            />
          )}
          {isBugColumnEnabled && (
            <InsightItem
              label="Bugs"
              value={stats.total_bugs}
              tooltip="Total number of bugs"
            />
          )}
          <InsightItem
            label="Vulnerabilities"
            value={stats.total_vulnerabilities}
            tooltip={`Total number of vulnerabilities, ${
              selectedStateFiltersList.length === 0
                ? 'including inactive repositories'
                : ''
            }`}
            isLast
          />
        </div>
      </Card>
    </div>
  );
};

export default RepoInsights;
